import * as Sentry from '@sentry/react';
import { QueryCache, QueryClient } from '@tanstack/react-query';
import { TIME, convertStringToSentryTag } from '../utils/helpers/common';
import { DEFAULT_QUERY_RETRY } from './queryRetry';

export { DEFAULT_QUERY_RETRY };

/**
 * Caching and refetching are not working predictably due to axios-cache-interceptor.
 * These settings have been added in anticipation of when it is removed.
 */
const QUERY_CLIENT_DEFAULT_OPTIONS = {
  gcTime: TIME.minutesToMs(15),
  refetchInterval: TIME.minutesToMs(5),
  staleTime: TIME.minutesToMs(5),
} as const;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      ...QUERY_CLIENT_DEFAULT_OPTIONS,
      /**
       * Using default 'online' networkMode instead of 'offlineFirst'
       * because 'offlineFirst' causes issues in Percy/Playwright environments
       * where network detection is unreliable, causing queries to pause indefinitely
       * and preventing page load events from firing.
       */
      refetchOnWindowFocus: false,
      retry: DEFAULT_QUERY_RETRY,
    },
  },
  queryCache: new QueryCache({
    onError(error, query) {
      // A query may mark an error it expects and shows inline, such as the
      // Titles page's too-broad client search, so it is not reported.
      const isExpectedError = query.meta?.isExpectedError;

      if (typeof isExpectedError === 'function' && isExpectedError(error)) {
        return;
      }

      Sentry.captureException(error, {
        contexts: {
          tanstackQuery: {
            queryKey: query.queryKey,
            state: query.state,
          },
        },
        tags: {
          queryHash: convertStringToSentryTag(query.queryHash),
          queryStatus: query.state.status,
          type: convertStringToSentryTag('queryClient'),
        },
      });
    },
  }),
});

// Workspaces seeded from the list (prefetch.ts) have no observer, so they'd be
// collected after 15 minutes and a later switch would show the old workspace.
queryClient.setQueryDefaults(['workspaces', 'single'], { gcTime: Infinity });
